import { useEffect, useMemo, useState } from "react";
import {
  grantingSpace,
  grantLabel,
  spaceColor,
} from "@/components/main/stores/orgContextStore";
import { useModelSpaceTrail } from "@/components/canvas/leftSide/useModelSpaceTrail";
import {
  fetchSpaceGrantsResult,
  SpaceGrant,
  SpaceGrantsResult,
} from "@/lib/api/organizations";
import { readClientId } from "@/lib/session/sessionWatcher";

export type SpaceRole = "Owner" | "Collaborator" | "Viewer";

export interface SpacePerson {
  /// The client id, as the direct list spells it.
  id: string;
  name: string;
  email: string;
  role: SpaceRole;
  isSelf: boolean;
}

type SpaceMark = { name: string; color: string; orgId: string };

export type SpaceAccess =
  | ({ state: "loading" } & SpaceMark)
  | ({ state: "failed"; retry: () => void } & SpaceMark)
  | ({ state: "ready"; people: SpacePerson[] } & SpaceMark);

// A space manager owns every model in the space.
const ROLE_OF: Record<string, SpaceRole> = {
  manager: "Owner",
  collaborator: "Collaborator",
  viewer: "Viewer",
};
const RANK: Record<SpaceRole, number> = {
  Owner: 3,
  Collaborator: 2,
  Viewer: 1,
};

export function spacePeople(
  grants: SpaceGrant[],
  selfId: string,
): SpacePerson[] {
  const byId = new Map<string, SpacePerson>();
  for (const grant of grants) {
    const role = ROLE_OF[grant.role];
    if (!role) continue;
    const id = grant.subject.replace(/^user:/, "");
    const held = byId.get(id);
    if (held && RANK[held.role] >= RANK[role]) continue;
    byId.set(id, {
      id,
      name: grantLabel(grant),
      email: grant.email ?? "",
      role,
      isSelf: id === selfId,
    });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/// Who reaches a model through its space, asked only when the space is among
/// the caller's visible ones, so someone shared only the model learns nothing.
export function useModelSpaceAccess(modelId: string): {
  space?: SpaceAccess;
  sealed: boolean;
} {
  const granting = grantingSpace(useModelSpaceTrail(modelId));
  const spaceId = granting?.space_id;
  const [result, setResult] = useState<SpaceGrantsResult>();
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!spaceId) return;
    let stale = false;
    setResult(undefined);
    void fetchSpaceGrantsResult(spaceId).then((answer) => {
      if (!stale) setResult(answer);
    });
    return () => {
      stale = true;
    };
  }, [spaceId, attempt]);

  const space = useMemo((): SpaceAccess | undefined => {
    if (!granting || result?.status === "refused") return undefined;
    const mark = {
      name: granting.name,
      color: spaceColor(granting),
      orgId: granting.org_id,
    };
    if (!result) return { state: "loading", ...mark };
    if (result.status === "failed")
      return {
        state: "failed",
        ...mark,
        retry: () => setAttempt((n) => n + 1),
      };
    const selfId = readClientId() ?? "";
    return {
      state: "ready",
      ...mark,
      people: spacePeople(result.grants, selfId),
    };
  }, [granting, result]);

  return { space, sealed: !!granting?.sealed };
}
